import { Accordion, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReportSignal } from '../../types/api';
import { DetailSection } from './DetailSection';
import { signalSource } from './facts/computedRecordTypes';
import { RecordCard } from './facts/RecordCard';
import {
    isImportMetadataSignal,
    signalToRecord,
    sortSignalsForDisplay,
} from './facts/signalTypes';

function SignalsAccordion({ signals }: { signals: ReportSignal[] }) {
    const { t } = useTranslation();
    const sorted = sortSignalsForDisplay(signals);

    if (sorted.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                {t('detail.noReportComputedSignals')}
            </Text>
        );
    }

    return (
        <Accordion variant="separated" multiple>
            {sorted.map((signal) => (
                <RecordCard
                    key={signal.id}
                    record={signalToRecord(signal)}
                    typeI18nPrefix="signalTypes"
                    source={signalSource(signal.signalValue ?? {})}
                />
            ))}
        </Accordion>
    );
}

export function ReportSignalsSection({ signals }: { signals: ReportSignal[] }) {
    const { t } = useTranslation();

    const { operational, importMetadata } = useMemo(() => {
        const operationalSignals: ReportSignal[] = [];
        const importSignals: ReportSignal[] = [];
        for (const signal of signals) {
            if (isImportMetadataSignal(signal.signalType)) {
                importSignals.push(signal);
            } else {
                operationalSignals.push(signal);
            }
        }
        return { operational: operationalSignals, importMetadata: importSignals };
    }, [signals]);

    return (
        <Stack gap="md">
            <DetailSection
                title={t('detail.reportOperationalSignals', { count: operational.length })}
                description={t('detail.reportOperationalSignalsHint')}
            >
                <SignalsAccordion signals={operational} />
            </DetailSection>
            {importMetadata.length > 0 ? (
                <DetailSection
                    title={t('detail.reportImportMetadata', { count: importMetadata.length })}
                    description={t('detail.reportImportMetadataHint')}
                >
                    <SignalsAccordion signals={importMetadata} />
                </DetailSection>
            ) : null}
        </Stack>
    );
}
